import useMenuBack from '../../hooks/useMenuBack'
import { ArrowLeft } from 'lucide-react'
import LegalMarkdown, { useLegalContent } from '../../components/LegalMarkdown'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * TermsConditionsScreen v242.6.0
 *
 * Renders admin-managed Terms & Conditions (markdown stored in app_config via the admin
 * panel, served by GET /api/v1/legal-content). Live DB content is the source of truth;
 * the hardcoded <FallbackTerms> below is shown only when the content is empty/unreachable
 * (offline). Previously this screen was 100% hardcoded and ignored admin edits entirely.
 */

const NAVY = '#15354F'

function LegalSection({ num, title, children }) {
  return (
    <div className="mb-3">
      <p style={{
        fontSize: 11, fontWeight: 700, color: NAVY, textTransform: 'uppercase',
        letterSpacing: '0.06em', padding: '10px 0 6px',
        borderBottom: `1.5px solid ${NAVY}`, marginBottom: 8,
      }}>
        {num}. {title}
      </p>
      <div style={{ fontSize: 11.5, color: '#475569', lineHeight: 1.6 }}>
        {children}
      </div>
    </div>
  )
}

// Offline fallback only — live admin content (above) wins whenever available.
function FallbackTerms() {
  return (
    <>
      <p style={{ fontSize: 10, color: '#94A3B8', marginBottom: 12 }}>
        Last updated: March 9, 2026 · Orella, Inc.
      </p>
      <div style={{
        padding: '10px 12px', borderRadius: 10,
        background: '#E9F8FF', border: '1px solid rgba(167,217,252,0.3)',
        marginBottom: 14,
      }}>
        <p style={{ fontSize: 10.5, color: NAVY, margin: 0, lineHeight: 1.5 }}>
          These Terms govern your use of the Orella Health platform. By creating an account or using the Platform, you agree to be bound by these Terms.
        </p>
      </div>

      <LegalSection num="1" title="Description of services">
        <p className="mb-2">Orella Health provides a technology platform that facilitates communication between patients and healthcare providers. Our services include multilingual symptom documentation across 11 languages, AI-assisted clinical documentation, appointment scheduling and coordination, care plan delivery, follow-up tracking, and transactional notifications via email, SMS, and in-app messaging.</p>
        <p style={{ fontWeight: 600, color: NAVY }}>Orella Health is not a healthcare provider. We do not provide medical advice, diagnosis, or treatment.</p>
      </LegalSection>

      <LegalSection num="2" title="Medical disclaimer">
        <p className="mb-2" style={{ fontWeight: 600, color: '#DC2626' }}>IMPORTANT: The Platform is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider. In case of a medical emergency, call 911 immediately.</p>
        <p>AI-generated content on the Platform — including pre-visit summaries, clinical analysis, and care plan documentation — is produced to assist healthcare providers and is always subject to physician review and approval. AI-generated content does not constitute medical advice.</p>
      </LegalSection>

      <LegalSection num="3" title="Eligibility & accounts">
        <p>To use the Platform, you must be at least 18 years of age. By creating an account, you represent that the information you provide is accurate, complete, and current. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>
      </LegalSection>

      <LegalSection num="4" title="SMS & email notifications">
        <p className="mb-2">By providing your phone number and email address, you consent to receive transactional communications related to your healthcare. Message frequency varies. Message and data rates may apply.</p>
        <p>To opt out of SMS, reply <strong>STOP</strong> to any message. For help, reply <strong>HELP</strong> or contact admin@orellahealth.com.</p>
      </LegalSection>

      <LegalSection num="5" title="User responsibilities">
        <p>You agree to: provide accurate and complete information about yourself and your health; maintain the security of your account credentials; use the Platform only for its intended purpose of healthcare communication; not use the Platform for any unlawful purpose; and report any unauthorized access immediately.</p>
      </LegalSection>

      <LegalSection num="6" title="AI-assisted features">
        <p>The Platform uses artificial intelligence to assist healthcare providers with clinical documentation. You acknowledge that AI-generated content is a tool to assist licensed providers, not a replacement for clinical judgment; all AI content is subject to physician review; and AI models may occasionally produce inaccurate or incomplete information.</p>
      </LegalSection>

      <LegalSection num="7" title="Intellectual property">
        <p>All content, features, and functionality of the Platform are the exclusive property of Orella, Inc. and are protected by copyright, trademark, and other intellectual property laws.</p>
      </LegalSection>

      <LegalSection num="8" title="Limitation of liability">
        <p>To the maximum extent permitted by law, Orella Health shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Platform, any clinical decisions made by healthcare providers, or any content obtained through the Platform.</p>
      </LegalSection>

      <LegalSection num="9" title="Disclaimer of warranties">
        <p>The Platform is provided on an "as is" and "as available" basis without warranties of any kind. We do not warrant that the Platform will be uninterrupted, error-free, or secure.</p>
      </LegalSection>

      <LegalSection num="10" title="Account termination">
        <p>We reserve the right to suspend or terminate your account at any time for violation of these Terms. You may delete your account at any time through the app settings.</p>
      </LegalSection>

      <LegalSection num="11" title="Governing law">
        <p>These Terms shall be governed by the laws of the State of California, without regard to its conflict of law provisions. Any dispute shall be resolved through binding arbitration in Los Angeles, California.</p>
      </LegalSection>

      <LegalSection num="12" title="Changes to terms">
        <p>We may modify these Terms at any time. Material changes will be communicated through the Platform or by email. Continued use constitutes acceptance of updated Terms.</p>
      </LegalSection>

      <LegalSection num="13" title="Contact">
        <p style={{ marginBottom: 4 }}>admin@orellahealth.com</p>
        <p>(323) 331-3909 · 700 S. Flower St, Suite 1000, Los Angeles, CA 90017</p>
      </LegalSection>
    </>
  )
}

export default function TermsConditionsScreen() {
  const goBack = useMenuBack()
  const { content, loading } = useLegalContent('terms')

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* Navy Header */}
      <div
        className="flex-shrink-0 flex items-center gap-2.5"
        style={{
          padding: '14px 16px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          background: 'linear-gradient(135deg, #15354F, #0F2438)',
        }}
      >
        <button
          onClick={() => goBack()}
          className="p-0"
          style={{ background: 'none', border: 'none' }}
        >
          <ArrowLeft size={18} color="rgba(255,255,255,0.6)" strokeWidth={2.5} />
        </button>
        <span className="flex-1" style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>
          Terms & Conditions
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-8 overflow-auto" style={{ paddingTop: 14 }}>
        {loading ? (
          <div className="flex items-center justify-center" style={{ paddingTop: 48 }}>
            <OrellaSpinner size={28} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : content ? (
          <LegalMarkdown text={content} hideTitle />
        ) : (
          <FallbackTerms />
        )}
      </div>
    </div>
  )
}
